import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getToken } from "../../utils/tokenUtils";

const MonthlyReports = () => {
  const [summary, setSummary] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(false);

  const token = getToken();

  // Fetch summaries
  const fetchSummary = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        "https://hrmsnew.prepstripe.com/api/payroll/summary",
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { month, year },
        },
      );
      setSummary(res.data || []);
    } catch (err) {
      console.error("Error fetching summary:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch payrolls
  const fetchPayrolls = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        "https://hrmsnew.prepstripe.com/api/payroll/monthly-payrolls",
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { month, year },
        },
      );
      setPayrolls(res.data || []);
    } catch (err) {
      console.error("Error fetching payrolls:", err);
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    const wsSummary = XLSX.utils.json_to_sheet(summary);
    const wsPayrolls = XLSX.utils.json_to_sheet(payrolls);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");
    XLSX.utils.book_append_sheet(wb, wsPayrolls, "Detailed Payrolls");

    XLSX.writeFile(wb, `Payroll_Report_${month}_${year}.xlsx`);
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();

    doc.text(`Payroll Report - ${month}/${year}`, 14, 15);

    // Summary Table
    doc.autoTable({
      startY: 25,
      head: [["Employee", "Total Salary", "Deductions", "Net Pay"]],
      body: summary.map((row) => [
        row.employee_name,
        row.total_salary,
        row.deductions,
        row.net_pay,
      ]),
    });

    // Detailed Payrolls Table
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 15,
      head: [["Employee", "Month", "Year", "Gross Pay", "Net Pay"]],
      body: payrolls.map((row) => [
        row.employee_name,
        row.month,
        row.year,
        row.gross_pay,
        row.net_pay,
      ]),
    });

    doc.save(`Payroll_Report_${month}_${year}.pdf`);
  };

  // Fetch on filter change
  useEffect(() => {
    if (month && year) {
      fetchSummary();
      fetchPayrolls();
    }
  }, [month, year]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold mb-6">📊 Monthly Payroll Reports</h1>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="border rounded-md px-3 py-2"
        >
          <option value="">Select Month</option>
          {[...Array(12)].map((_, i) => (
            <option key={i + 1} value={i + 1}>
              {new Date(0, i).toLocaleString("default", { month: "long" })}
            </option>
          ))}
        </select>

        <select
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="border rounded-md px-3 py-2"
        >
          <option value="">Select Year</option>
          {[2023, 2024, 2025].map((yr) => (
            <option key={yr} value={yr}>
              {yr}
            </option>
          ))}
        </select>

        <button
          onClick={() => {
            fetchSummary();
            fetchPayrolls();
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Apply Filters
        </button>
      </div>

      {/* Export Buttons */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={exportToExcel}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Export Excel
        </button>
        <button
          onClick={exportToPDF}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Export PDF
        </button>
      </div>

      {loading && <p className="text-gray-600">Loading...</p>}

      {/* Summary Table */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-3">Summary</h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-white shadow-md rounded-lg">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2 text-left">Employee</th>
                <th className="border px-4 py-2 text-left">Total Salary</th>
                <th className="border px-4 py-2 text-left">Deductions</th>
                <th className="border px-4 py-2 text-left">Net Pay</th>
              </tr>
            </thead>
            <tbody>
              {summary.length > 0 ? (
                summary.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="border px-4 py-2">{row.employee_name}</td>
                    <td className="border px-4 py-2">{row.total_salary}</td>
                    <td className="border px-4 py-2">{row.deductions}</td>
                    <td className="border px-4 py-2">{row.net_pay}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-3">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detailed Payroll Table */}
      <div>
        <h2 className="text-xl font-semibold mb-3">Detailed Payrolls</h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-white shadow-md rounded-lg">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2 text-left">Employee</th>
                <th className="border px-4 py-2 text-left">Month</th>
                <th className="border px-4 py-2 text-left">Year</th>
                <th className="border px-4 py-2 text-left">Gross Pay</th>
                <th className="border px-4 py-2 text-left">Net Pay</th>
              </tr>
            </thead>
            <tbody>
              {payrolls.length > 0 ? (
                payrolls.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="border px-4 py-2">{row.employee_name}</td>
                    <td className="border px-4 py-2">{row.month}</td>
                    <td className="border px-4 py-2">{row.year}</td>
                    <td className="border px-4 py-2">{row.gross_pay}</td>
                    <td className="border px-4 py-2">{row.net_pay}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-3">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MonthlyReports;
